/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { addDirtyState } from 'src/core/utils/decorators/addDirtyState';
import { getDomainService } from 'src/luis/core/services/domains/domainService';
import { domainSwaggerToDomain } from 'src/luis/modules/domains/domainMappers';
import { DomainStore } from 'src/luis/types/stores/domains/DomainStore';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { IntentStore } from 'src/luis/types/stores/intents/IntentStore';

export type DomainHandlerGroup = ReturnType<typeof createDomainHandlerGroup>;

/**
 * Creates a handler group of functions for the domains to
 * be fired by the dispatcher.
 *
 * @param dependencies The dependencies required by this factory.
 */
export const createDomainHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    /**
     * Get the prebuilt domains.
     *
     * @param param0.culture The culture of the current application.
     */
    const loadDomains = async ({ culture, context }: HandlerParams<{ culture: string }>) => {
        const appStore = rootStore.luisAppStoreHouse.findById(context.appId);
        const { domainStoreHouse } = appStore;

        const {
            entityStoreHouse: { items: entities },
            intentStoreHouse: { items: intents }
        } = rootStore.currentLuisAppStore.currentAppVersionStore;

        if (!context.forceRefresh && domainStoreHouse.isLoading) {
            return;
        }

        /**
         * To refresh the button state every time the page loaded
         */
        domainStoreHouse.items.forEach(domain => {
            const isAdded = isDomainAdded(domain, { entities, intents });
            domain.setDomainState(isAdded ? 'added' : 'notAdded');
        });

        if (domainStoreHouse.isLoaded) {
            return;
        }

        try {
            domainStoreHouse.setIsLoading(true);

            const domainSwaggers = await getDomainService({ context }).getDomains(culture);
            const domains = domainSwaggers.map(domainSwaggerToDomain);

            const statefulDomains = domains.map(domain => {
                const isAdded = isDomainAdded(domain, { entities, intents });
                domain.state = isAdded ? 'added' : 'notAdded';

                return domain;
            });

            domainStoreHouse.setItems(statefulDomains);
            domainStoreHouse.setIsLoaded(true);
        } finally {
            domainStoreHouse.setIsLoading(false);
        }
    };

    /**
     * Add / remove domain to / from the current application.
     *
     * @param param0.name The name of the domain to be added.
     * @param param0.culture The culture of the domain to be added.
     */
    const toggleDomain = async ({ name, culture, context }: HandlerParams<Pick<DomainStore, 'name' | 'culture'>>) =>
        flow(async () => {
            const appStore = rootStore.luisAppStoreHouse.findById(context.appId);

            const domainStore = appStore.domainStoreHouse.findByName(name);
            const domainService = getDomainService({ context });

            const isAdded = domainStore.state === 'added';

            domainStore.setDomainState('loading');

            await (isAdded ? domainService.removeDomain({ name }) : domainService.addDomain({ name, culture }));

            domainStore.setDomainState(isAdded ? 'notAdded' : 'added');
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    return { loadDomains, toggleDomain };
};

/**
 * Helper function to check whether the given domain is
 * added to the current application of not.
 *
 * @todo This function contains incorrect logic. This will fail
 * as soon as the user renames any of the prebuilt entities or
 * intents. Backend returns two properties with any model that
 * is a prebuilt domain intent or a prebuilt domain entity:
 *
 * - customPrebuiltDomainName
 * - customPrebuiltModelName
 *
 * These stay constant even if the user renames the entity or
 * intent. The system currently does not support this. There is
 * a backlog item to support this: Feature 16414
 *
 * @param domain The domain to check
 * @param param1.entities The entities store items
 * @param param1.intents The intents store items
 */
export const isDomainAdded = (domain: DomainStore, { entities, intents }: { entities: EntityStore[]; intents: IntentStore[] }): boolean => {
    const currentAppIntents = intents.map(item => item.name);
    const currentAppEntities = entities.map(item => item.name);

    return (
        domain.intents.every(intent => currentAppIntents.includes(`${domain.name}.${intent.name}`)) &&
        /**
         * @todo Logic of matching the entities has to be modified
         */
        domain.entities.every(
            entity => currentAppEntities.includes(`${domain.name}.${entity.name}`) || currentAppEntities.includes(entity.name)
        )
    );
};
